import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  BookOpen,
  FileText,
  BookMarked,
  Video,
  PenTool,
  Users,
  ArrowRight,
  Download,
  PlayCircle,
  Calendar
} from 'lucide-react';
import { ROUTES } from '@/lib/constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/lib/translations';
import { animations } from '@/lib/animations';

const ResourcesPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];

  const resources = {
    documentation: [
      {
        title: 'Getting Started Guide',
        description: 'Learn the basics of implementing our AI automation solution',
        icon: BookOpen,
        link: '#'
      },
      {
        title: 'API Documentation',
        description: 'Detailed API reference for developers',
        icon: FileText,
        link: '#'
      },
      {
        title: 'Integration Guide',
        description: 'Step-by-step guide for system integration',
        icon: BookMarked,
        link: '#'
      }
    ],
    caseStudies: [
      {
        title: 'Financial Services Success Story',
        description: '45% reduction in customer service response time',
        industry: 'Financial Services',
        link: '#'
      },
      {
        title: 'Healthcare Provider Transformation',
        description: '60% automation of patient inquiries',
        industry: 'Healthcare',
        link: '#'
      },
      {
        title: 'Retail Chain Optimization',
        description: '80% increase in customer satisfaction',
        industry: 'Retail',
        link: '#'
      }
    ],
    webinars: [
      {
        title: 'AI Automation Best Practices',
        date: 'March 15, 2024',
        description: 'Learn from industry experts about implementing AI automation',
        registration: true
      },
      {
        title: 'Future of Customer Service',
        date: 'March 22, 2024',
        description: 'Discover how AI is transforming customer service',
        recording: true
      },
      {
        title: 'ROI of AI Implementation',
        date: 'March 29, 2024',
        description: 'Calculate and maximize your AI investment returns',
        registration: true
      }
    ],
    articles: [
      {
        title: 'The Impact of AI on Business Efficiency',
        category: 'Industry Insights',
        readTime: '5 min read'
      },
      {
        title: 'How to Prepare Your Team for AI Integration',
        category: 'Implementation',
        readTime: '7 min read'
      },
      {
        title: 'Measuring AI Automation Success',
        category: 'Best Practices',
        readTime: '6 min read'
      }
    ]
  };

  return (
    <div className="flex flex-col min-h-screen">
      <section className="flex flex-col items-center justify-center py-16 md:py-24 relative overflow-hidden w-full bg-gradient-to-b from-primary/5 to-transparent">
        <div className="relative mx-auto flex max-w-[1200px] flex-col items-center gap-8 text-center px-4">
          <h1 className={`text-4xl font-bold leading-tight tracking-tighter md:text-6xl lg:leading-[1.1] ${animations.fadeInSlideUp}`}>
            Resources & Learning Center
          </h1>
          <p className={`max-w-[750px] text-lg text-muted-foreground md:text-xl ${animations.fadeInSlideUp} ${animations.delay200}`}>
            Explore our comprehensive resources to help you succeed with AI automation
          </p>
        </div>
      </section>

      {/* Documentation Section */}
      <section className="container py-12">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-bold mb-8">Technical Documentation</h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {resources.documentation.map((doc, index) => {
              const Icon = doc.icon;
              return (
                <Card
                  key={doc.title}
                  className={`p-6 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex flex-col gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{doc.title}</h3>
                      <p className="text-muted-foreground mb-4">{doc.description}</p>
                      <Button variant="outline" className="w-full group" asChild>
                        <a href={doc.link}>
                          Read Documentation
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="container py-12 bg-muted/30">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-bold mb-8">Case Studies</h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {resources.caseStudies.map((study, index) => (
              <Card
                key={study.title}
                className={`p-6 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex flex-col gap-4">
                  <span className="text-sm font-medium text-primary">{study.industry}</span>
                  <h3 className="text-xl font-bold">{study.title}</h3>
                  <p className="text-muted-foreground">{study.description}</p>
                  <Button variant="outline" className="w-full group" asChild>
                    <a href={study.link}>
                      Read Case Study
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Webinars Section */}
      <section className="container py-12">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-bold mb-8">Webinars & Events</h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {resources.webinars.map((webinar, index) => (
              <Card
                key={webinar.title}
                className={`p-6 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2 text-primary">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm font-medium">{webinar.date}</span>
                  </div>
                  <h3 className="text-xl font-bold">{webinar.title}</h3>
                  <p className="text-muted-foreground">{webinar.description}</p>
                  <Button 
                    variant={webinar.registration ? "default" : "outline"}
                    className="w-full group"
                  >
                    {webinar.registration ? (
                      <>
                        Register Now
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    ) : (
                      <>
                        Watch Recording
                        <PlayCircle className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="container py-12 bg-muted/30">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-bold mb-8">Latest Articles</h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {resources.articles.map((article, index) => (
              <Card
                key={article.title}
                className={`p-6 hover:shadow-lg transition-all ${animations.fadeInSlideUp}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{article.category}</span>
                    <span>{article.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold">{article.title}</h3>
                  <Button variant="outline" className="w-full group">
                    Read Article
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="container py-24">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Stay Updated</h2>
          <p className="text-lg text-muted-foreground mb-8">
            Subscribe to our newsletter to receive the latest resources, articles, and updates
          </p>
          <form className="flex gap-4 max-w-[500px] mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-2 rounded-md border border-border bg-background"
            />
            <Button type="submit" className="group">
              Subscribe
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage; 